import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LOANS_STATUS, OFFER_STATUS } from 'constants/offer';

import type { TOffer } from 'shared/types/TRankingService';

import type { IOffersState } from '../types';

import { getStatusesAPI } from './fetchOffersStatuses.query';
import { setPreApprovingOffersData } from './setPreApprovingOffersData';

const initialState: IOffersState = {
  calcId: '',
  offers: [],
  isLoading: true,
  isErrorResponse: false,
  isBentoRankingModel: false,
  offersStatuses: {},
  banksWithActiveStatuses: [],
  isAvailableMpl: undefined,
  availableBanks: [],
};

export const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setOffers: (state, { payload }: PayloadAction<IOffersState>) => {
      // подмешиваем статусы, пришедшие с сервера, в текущие статусы на клиенте,
      // так как loans не сразу возвращает новые статусы, а через 10-15 секунд
      // и если мы их не подмешаем, то при обновлении списка офферов
      // продукты будут отображаться без активной заявки
      const currentStatuses = {
        ...state.offersStatuses,
        ...payload.offersStatuses,
      };

      const currentBanksWithActiveStatuses = Array.from(
        new Set([...state.banksWithActiveStatuses, ...payload.banksWithActiveStatuses]),
      );

      state.offers = payload.offers.map((offer) => {
        // если оффер в статусе PreApproved, то НЕ его переносим в Active
        if (offer.loansStatus === LOANS_STATUS.PREAPPROVED) {
          return offer;
        }
        if (currentStatuses[offer.id]) {
          // если статус уже есть в текущих статусах, то добавляем его к офферу и переводим в группу Active
          return { ...offer, status: currentStatuses[offer.id], group: 'Active' };
        }
        // если статус не найден, но банк с активным статусом есть в текущих банках,
        // то добавляем статус ALREADY_SENT_FROM_SAME_BANK,
        // чтобы не показывать кнопку отправки заявки
        if (!currentStatuses[offer.id] && currentBanksWithActiveStatuses.includes(offer.bankId)) {
          return { ...offer, status: OFFER_STATUS.ALREADY_SENT_FROM_SAME_BANK };
        }
        return offer;
      });

      state.isLoading = false;
      state.calcId = payload.calcId;
      state.isBentoRankingModel = payload.isBentoRankingModel ?? false;
      state.offersStatuses = currentStatuses;
      state.banksWithActiveStatuses = currentBanksWithActiveStatuses;
      state.availableBanks = payload.availableBanks;
    },
    addOffer: (state, { payload }: PayloadAction<TOffer>) => {
      state.offers = [...state.offers, payload];
      state.isLoading = false;
    },
    updateOfferData: (state, { payload }: PayloadAction<{ id: string } & Partial<Omit<TOffer, 'id'>>>) => {
      const { id, ...offerData } = payload;

      state.offers = state.offers.map((offer) => {
        if (offer.id === id) {
          // если в обновлении данных есть статус, то добавляем его в offersStatuses
          // и добавляем банк в banksWithActiveStatuses, если его там нет
          if (offerData.status) {
            state.offersStatuses = {
              ...state.offersStatuses,
              [id]: offerData.status,
            };
            state.banksWithActiveStatuses = Array.from(new Set([...state.banksWithActiveStatuses, offer.bankId]));
          }

          return { ...offer, ...offerData };
        }
        return offer;
      });
      state.isLoading = false;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },

    setIsAvailableMpl: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.isAvailableMpl = payload;
    },

    setIsErrorResponse: (state, { payload }: PayloadAction<boolean>) => {
      state.isErrorResponse = payload;
    },

    clearGroup: (state, { payload }: PayloadAction<TOffer['group']>) => {
      state.offers = state.offers.filter((offer) => offer.group !== payload);
    },

    disableOffersFromSameBank: (state, { payload }: PayloadAction<{ offerId: string; bankId: string }>) => {
      const { offerId, bankId } = payload;

      state.offers = state.offers.filter((offer) => {
        if (offer.bankId === bankId && offer.id !== offerId) {
          return false;
        }
        return true;
      });
    },
    setAvailableBanks: (state, { payload }: PayloadAction<string[]>) => {
      return {
        ...state,
        availableBanks: payload,
      };
    },
    setPreApprovingOffers: setPreApprovingOffersData,
  },
  extraReducers: (builder) => {
    builder.addMatcher(getStatusesAPI.endpoints.getStatuses.matchFulfilled, (state, { payload }) => {
      if (Array.isArray(payload)) {
        const statusMap = new Map(payload.map((status) => [status.productId, status]));

        state.offers = state.offers.map((offer) => {
          if (offer.id && statusMap.has(offer.id)) {
            const statusInfo = statusMap.get(offer.id) || {};
            if (statusInfo?.status) {
              // если при поллинге пришел новый статус, то добавляем его в offersStatuses
              // и добавляем банк в banksWithActiveStatuses, если его там нет
              state.offersStatuses = {
                ...state.offersStatuses,
                [offer.id]: statusInfo.status,
              };
              state.banksWithActiveStatuses = Array.from(new Set([...state.banksWithActiveStatuses, offer.bankId]));

              if (statusInfo?.offerView) {
                return {
                  ...offer,
                  ...statusInfo.offerView.groupItem,
                  bankId: statusInfo.offerView.organization?.id,
                  bankName: statusInfo.offerView.organization?.name,
                  logo: statusInfo.offerView.organization?.logo,
                  phones: statusInfo.offerView.organization?.phones,
                  status: statusInfo?.status,
                  summaries: statusInfo.offerView.groupItem?.summaries?.map((summary: any) => ({
                    label: summary?.text,
                    value: String(summary?.value),
                  })),
                };
              }

              return { ...offer, status: statusInfo?.status };
            }
          }
          return offer;
        });
      }
    });
    builder.addMatcher(getStatusesAPI.endpoints.getActiveStatusesV2.matchFulfilled, setPreApprovingOffersData);
  },
});

export const {
  setOffers,
  setLoading,
  setIsAvailableMpl,
  addOffer,
  updateOfferData,
  clearGroup,
  disableOffersFromSameBank,
  setIsErrorResponse,
  setPreApprovingOffers,
  setAvailableBanks,
} = offersSlice.actions;
